import { EMaxAuctionService } from '@cian/api-models/search-offers/v2/search-offers-desktop.data';
import { Link } from '@cian/link-component';
import { VasComponent } from '@cian/vas-tooltip-component';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { TimeLabel } from '../time_label';
import { IOffer } from '../../../types/offer';
import { IUser } from '../../../types/user';
import { getOfferVasList } from '../../../utils/offer_vas';
import { getVasLabel } from '../../../utils/getVasLabel';
import { AuctionPopup, TAuctionPopupKind, AuctionPopupKind } from '../AuctionPopup';
import { AuctionApplyNewBetHint } from '../AuctionApplyNewBetHint';
import * as styles from './RightPanel.css';
import { getAucionPopupTypeForNotOwn, getServiceName } from './utils';

export interface IRightPanelProps {
}

export interface IRightPanelState {
  isAuctionPopupShow: boolean;
  auctionPopupKind: TAuctionPopupKind;
  initialBet: number;
  isFromOwn: boolean;
}

export interface IRightPanelContext {
  offer: IOffer;
  user: IUser | null;
  /** Перечень регионов ранжированного поиска */
  mlSearchRegions?: number[];
  /** ML-ранжирование, без проверки региона  */
  isMlSearchForAll?: boolean;
  /** Максимальная ставка аукциона на выдаче */
  maxAuctionBet: number;
  /** Максимальная услуга выдачи */
  maxAuctionService?: EMaxAuctionService;
}

export interface IAuctionBetManagerEventDetail {
  currentBet: number;
  userOfferId: number;
}

export interface IAuctionRightPanelRerenderEventDetail {
  offerId: number;
}

export class RightPanel extends React.PureComponent<IRightPanelProps, IRightPanelState> {
  public context: IRightPanelContext;

  public static contextTypes = {
    mlSearchRegions: PropTypes.array,
    isMlSearchForAll: PropTypes.bool,
    offer: PropTypes.object,
    user: PropTypes.object,
    maxAuctionBet: PropTypes.number,
    maxAuctionService: PropTypes.string,
  };

  public state = {
    isAuctionPopupShow: false,
    auctionPopupKind: null,
    initialBet: 0,
    isFromOwn: false,
  };

  public componentWillMount() {
    const { auction } = this.context.offer;

    if (auction && auction.isOwn && auction.isEditable && typeof document !== 'undefined') {
      document.addEventListener('auctionBetManagerShow', this.handleAuctionBetManagerShow);
      document.addEventListener('auctionRightPanelRerender', this.handleAuctionRightPanelRerender);
    }
  }

  public render() {
    const { offer, user, maxAuctionBet, mlSearchRegions, isMlSearchForAll, maxAuctionService } = this.context;
    const { isAuctionPopupShow, auctionPopupKind, initialBet, isFromOwn } = this.state;

    const {
      geo,
      auction,
      isAuction,
      added,
      humanizedTimedelta,
      id,
    } = offer;

    const offerLocation = geo && geo.address && geo.address.length > 0
      ? geo.address.find(addr => addr.type === 'location')
      : null;

    const isMLSearchByRegion = !!offerLocation && (mlSearchRegions || []).includes(offerLocation.id);
    const isMLSearch = isMlSearchForAll || isMLSearchByRegion;

    const vasList = getOfferVasList(offer);
    const vasLabel = getVasLabel(offer, isMlSearchForAll);

    const auctionBet = auction && auction.currentBet;

    const isAgentUser = !!user && user.isAgent || false;

    return (
      <div onClick={e => e.stopPropagation()}
           className={styles['right-panel']}>
        <div className={styles['time']}>
          <TimeLabel added={added} humanizedTime={humanizedTimedelta}/>

          {isAuctionPopupShow && auctionPopupKind !== null &&
            <AuctionPopup
              kind={auctionPopupKind}
              initialBet={initialBet}
              isFromOwn={isFromOwn}
              onClose={this.handleAuctionPopupClose}
            />
          }
        </div>

        {isAgentUser &&
          <div className={styles['vas_label']}>
            <div className="vas_component_wrapper">
              <VasComponent
                uniqueKey={id}
                direction="left"
                label={vasLabel}
                vasList={vasList}
                isMLSearch={isMLSearch}
                auctionBet={auctionBet}
              />

              {auction && auction.isOwn && auction.isEditable && typeof auction.nextBet === 'number' &&
                <AuctionApplyNewBetHint
                  nextBet={auction.nextBet}
                  applyNewBetDate={auction.applyNewBetDate}
                />
              }
            </div>

            {auction && auction.isOwn && auction.isEditable &&
              <Link
                className={styles['auction_link']}
                onClick={this.showAuctionPopup(
                  getServiceName<IOffer>(offer).toLowerCase() === `is${maxAuctionService}`
                    ? AuctionPopupKind.MANAGE
                    : AuctionPopupKind.NOT_PREMIUM,
                  maxAuctionBet,
                  true,
                )}
              >
                Поднять объявление в поиске
              </Link>
            }

            {auction && isAuction && !auction.isOwn &&
              <Link
                className={styles['auction_link']}
                onClick={this.showAuctionPopup(getAucionPopupTypeForNotOwn(offer))}
              >
                Как опередить это объявление?
              </Link>
            }
          </div>
        }
      </div>
    );
  }

  public componentWillUnmount() {
    /* istanbul ignore next */
    if (typeof document === 'undefined') {
      return;
    }

    document.removeEventListener('auctionBetManagerShow', this.handleAuctionBetManagerShow);
    document.removeEventListener('auctionRightPanelRerender', this.handleAuctionRightPanelRerender);
  }

  private handleAuctionBetManagerShow = (event: CustomEvent<IAuctionBetManagerEventDetail>) => {
    const { currentBet, userOfferId } = event.detail;
    const { offer } = this.context;

    if (offer.id !== userOfferId) {
      return;
    }

    const node = ReactDOM.findDOMNode(this) as Element;
    node.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });

    this.showAuctionPopup(AuctionPopupKind.MANAGE, currentBet)();
  }

  private showAuctionPopup = (
    auctionPopupKind: TAuctionPopupKind,
    initialBet: number = 0,
    isFromOwn: boolean = false,
  ) => () => {
    const event = new Event('auctionNotificationHide');
    document.dispatchEvent(event);

    this.setState({
      isAuctionPopupShow: true,
      auctionPopupKind,
      initialBet,
      isFromOwn,
    });
  }

  private handleAuctionPopupClose = () => {
    this.setState({ isAuctionPopupShow: false, auctionPopupKind: null });
  }

  private handleAuctionRightPanelRerender = (event: CustomEvent<IAuctionRightPanelRerenderEventDetail>) => {
    const { offerId } = event.detail;
    const { offer } = this.context;

    if (offer.id !== offerId) {
      return;
    }

    // Делаем принудительный rerender
    this.forceUpdate();
  }
}
